import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { FaWhatsapp, FaMapMarkerAlt, FaCalendarAlt, FaUsers, FaStar, FaCheck, FaTimes, FaBus, FaClock } from 'react-icons/fa';
import BookingForm from '../../components/common/BookingForm';
import { companyInfo } from '../../data/tourData';
import './TungnathTour.css';

const TungnathTour = () => {
  return (
    <>
      <Helmet>
        <title>Tungnath Tour - NomadTrailz</title>
        <meta name="description" content="Experience the sacred Tungnath pilgrimage with NomadTrailz. Trek to the world's highest Shiva temple and enjoy breathtaking Himalayan views." />
      </Helmet>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        {/* Hero Section */}
        <section className="destination-hero">
          <div className="hero-background">
            <img 
              src="https://res.cloudinary.com/duym2bgp2/image/upload/v1754934084/hua4lwsxtzobrjkxqorr.webp" 
              alt="Tungnath" 
            />
            <div className="hero-overlay"></div>
          </div>
          <div className="hero-content">
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              Tungnath Tour
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Sacred pilgrimage to the world's highest Shiva temple at Tungnath
            </motion.p>
          </div>
        </section>

        {/* Tour Details */}
        <section className="tour-details">
          <div className="container">
            <div className="details-grid">
              <div className="tour-info">
                <motion.div 
                  className="info-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <h2>Tour Overview</h2>
                  <p>
                    Tungnath is the highest Shiva temple in the world and one of the five sacred temples of Panch Kedar. 
                    Located at an altitude of 3,680 meters, this spiritual journey takes you through the beautiful 
                    Kedarnath Wildlife Sanctuary to reach the ancient temple where Lord Shiva's arms are worshipped. 
                    Experience the divine energy and breathtaking views of the Himalayas with comfortable accommodations 
                    and expert guidance.
                  </p>
                </motion.div>

                <motion.div 
                  className="highlights-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <h3>Highlights</h3>
                  <ul>
                    <li>World's highest Shiva temple at Tungnath</li>
                    <li>Chandrashila peak trek (if weather permits)</li>
                    <li>Deoriatal lake trek and scenic views</li>
                    <li>Devprayag Sangam visit</li>
                    <li>Maa Dhari Devi Temple darshan</li>
                    <li>Kedarnath Wildlife Sanctuary trekking</li>
                    <li>Swiss camp accommodation in Chopta</li>
                  </ul>
                </motion.div>

                <motion.div 
                  className="itinerary-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <h3>Itinerary</h3>
                  <div className="day-item">
                    <h4>Day 0: Departure From Delhi To Chopta</h4>
                    <ul>
                      <li>Depart from Delhi's Jor Bagh Metro Station for an overnight journey to Chopta Tungnath</li>
                      <li>Start your journey - ice breaking session and get to know your co-travellers</li>
                      <li>Overnight journey to World's Highest Shiva Temple</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 1: Devprayag | Dhari Devi Temple | Chopta Arrival</h4>
                    <ul>
                      <li>Catch some tourist attractions on the way to Chopta</li>
                      <li>Visit Devprayag (Sangam of Bhagirathi and Alaknanda)</li>
                      <li>Visit Maa Dhari Devi Temple</li>
                      <li>Reach Chopta, Cottages / Swiss camp Check-in</li>
                      <li>Freshen up & rest for some time</li>
                      <li>Enjoy the views in the lap of nature</li>
                      <li>Followed by dinner and overnight stay</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 2: Chopta Tungnath Chandrashila Trek</h4>
                    <ul>
                      <li>After breakfast start your drive towards trek point via lush green Kedarnath Wildlife Sanctuary</li>
                      <li>Start your trek towards Tungnath Temple which will be around 3.5 kms</li>
                      <li>Reach Tungnath & pay your respect at World's Highest Shiva Temple</li>
                      <li>Start your journey to most astonished Chandrashila peak (if weather permits)</li>
                      <li>Come back to your stay by evening, have dinner and sleep overnight</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 3: Deoriatal Trek | Rishikesh | Delhi Depart</h4>
                    <ul>
                      <li>Wake up with beautiful mountain ranges view</li>
                      <li>Have Breakfast & Check-out from stay</li>
                      <li>Board your vehicle and Drive for Sari Village (Base point for DeoriaTal trek)</li>
                      <li>Reach Deoria Tal and enjoy the picturesque views</li>
                      <li>Trek back to Sari Village</li>
                      <li>Departure from Deoria Tal to Delhi via Rishikesh (Rishikesh halt is time allows)</li>
                      <li>Delhi Arrival next day early morning by 5 AM</li>
                    </ul>
                  </div>
                </motion.div>

                <motion.div 
                  className="inclusions-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                >
                  <h3>Inclusions</h3>
                  <ul>
                    <li><FaCheck className="check-icon" />Transportation from Delhi to Delhi by AC coach</li>
                    <li><FaCheck className="check-icon" />Accommodation for two nights in camps/rooms/cottages</li>
                    <li><FaCheck className="check-icon" />Meals (2 Breakfast, Evening Snacks and 2 Dinners)</li>
                    <li><FaCheck className="check-icon" />The Trip Captain will be present at all times during the trip</li>
                    <li><FaCheck className="check-icon" />All required Permits</li>
                    <li><FaCheck className="check-icon" />Driver Allowance, Toll Taxes and other state taxes</li>
                    <li><FaCheck className="check-icon" />24/7 customer support</li>
                  </ul>
                </motion.div>

                <motion.div 
                  className="exclusions-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                >
                  <h3>Exclusions</h3>
                  <ul>
                    <li><FaTimes className="times-icon" />Any extra meals apart from the ones mentioned under inclusions</li>
                    <li><FaTimes className="times-icon" />Travel Insurance, items of personal nature like porterage, tips, laundry etc</li>
                    <li><FaTimes className="times-icon" />Any entry fees, extra expenses for optional activities, room heaters or tickets, unless quoted in Inclusions</li>
                    <li><FaTimes className="times-icon" />Cost of snow-chained or 4*4 Vehicle if needed due to heavy snowfall</li>
                    <li><FaTimes className="times-icon" />Any other costing involved due to circumstances like natural calamity, weather conditions, riots, roadblocks, landslides or any other forced circumstances which are out of our control</li>
                    <li><FaTimes className="times-icon" />GST (5%) is applicable extra</li>
                    <li><FaTimes className="times-icon" />Anything which is not mentioned in the above inclusions</li>
                  </ul>
                </motion.div>
              </div>

              <div className="booking-section">
                <motion.div 
                  className="booking-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <h3>Book This Tour</h3>
                  <div className="price-info">
                    <span className="price">₹ 4,999</span>
                    <span className="duration">3 Days / 2 Nights</span>
                  </div>
                  
                  <div className="tour-features">
                    <div className="feature">
                      <FaMapMarkerAlt />
                      <span>Tungnath, Uttarakhand</span>
                    </div>
                    <div className="feature">
                      <FaCalendarAlt />
                      <span>Best Time: May to October</span>
                    </div>
                    <div className="feature">
                      <FaUsers />
                      <span>Group Size: 10-15 people</span>
                    </div>
                    <div className="feature">
                      <FaStar />
                      <span>Difficulty: Moderate</span>
                    </div>
                  </div>

                  <div className="pricing-options">
                    <h4>Cost Per Person</h4>
                    <div className="price-option">
                      <span className="option">QUAD SHARING</span>
                      <span className="amount">₹ 4,499 + 5% GST</span>
                    </div>
                    <div className="price-option">
                      <span className="option">TRIPLE SHARING</span>
                      <span className="amount">₹ 4,999 + 5% GST</span>
                    </div>
                    <div className="price-option">
                      <span className="option">DOUBLE SHARING</span>
                      <span className="amount">₹ 5,499 + 5% GST</span>
                    </div>
                  </div>
                </motion.div>

                <motion.div 
                  className="tour-logistics-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                >
                  <h3>Tour Logistics</h3>
                  <div className="logistics-grid">
                    <div className="logistics-item">
                      <FaBus className="logistics-icon" />
                      <div className="logistics-content">
                        <span className="logistics-label">Pickup Point:</span>
                        <span className="logistics-value">Delhi: ISBT Kashmere Gate</span>
                      </div>
                    </div>
                    <div className="logistics-item">
                      <FaClock className="logistics-icon" />
                      <div className="logistics-content">
                        <span className="logistics-label">Pickup Time:</span>
                        <span className="logistics-value">09:00 PM to 10:00 PM</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <BookingForm 
                  tourName="Tungnath Sacred Trek"
                  tourPrice="₹ 4,999"
                  tourDuration="3 Days / 2 Nights"
                />
              </motion.div>
            </div>
          </div>
        </section>
      </motion.div>
    </>
  );
};

export default TungnathTour;
